import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import '../crm.css';

const WINDOWS = [7, 30, 90];

function Table({ title, rows, t }) {
  if (rows.length === 0) return null;
  return (
    <section className="crm-card">
      <h2>{title}</h2>
      <div className="crm-table-wrap">
        <table className="crm-table">
          <thead>
            <tr>
              <th>{t('crm.stats.label')}</th>
              <th className="crm-num">{t('crm.stats.leads')}</th>
              <th className="crm-num">{t('crm.stats.won')}</th>
              <th className="crm-num">{t('crm.stats.value')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td className="crm-break">{row.label}</td>
                <td className="crm-num">{row.leads}</td>
                <td className="crm-num">{row.won}</td>
                <td className="crm-num">{row.value || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function CrmStatsPage() {
  const { t } = useTranslation('common');
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.getCrmStats(days).then(setStats).catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [days]);

  if (error) return <div className="crm-page"><div className="crm-error">{String(error)}</div></div>;
  if (!stats) return <div className="crm-page"><div className="spinner" /></div>;

  const tiles = [
    { label: t('crm.stats.total'), value: stats.total },
    { label: t('crm.stats.wonValue'), value: stats.won_value },
    {
      label: t('crm.stats.medianFirstContact'),
      value: stats.median_minutes_to_first_contact === null ? '—' : `${stats.median_minutes_to_first_contact} ${t('crm.stats.min')}`,
    },
    { label: t('crm.stats.withinSla'), value: `${stats.called_within_sla} / ${stats.total}` },
    { label: t('crm.stats.neverCalled'), value: stats.never_called, alarm: stats.never_called > 0 },
  ];

  return (
    <div className="crm-page">
      <header className="crm-head">
        <div>
          <h1 className="crm-title">{t('crm.stats.title')}</h1>
          <p className="crm-sub">{t('crm.stats.subtitle')}</p>
        </div>
        <div className="crm-tabs">
          {WINDOWS.map((window) => (
            <button
              key={window}
              type="button"
              className={`crm-tab${days === window ? ' on' : ''}`}
              onClick={() => setDays(window)}
            >
              {t('crm.stats.days', { count: window })}
            </button>
          ))}
        </div>
      </header>

      <div className="crm-tiles">
        {tiles.map((tile) => (
          <div key={tile.label} className={`crm-tile${tile.alarm ? ' alarm' : ''}`}>
            <span className="crm-tile-value">{tile.value}</span>
            <span className="crm-tile-label">{tile.label}</span>
          </div>
        ))}
      </div>

      <section className="crm-card">
        <h2>{t('crm.stats.funnel')}</h2>
        <ul className="crm-funnel">
          {Object.entries(stats.counts_by_status).map(([status, count]) => (
            <li key={status}>
              <span className={`crm-status-dot crm-status-${status}`} />
              {t(`crm.status.${status}`)}
              <strong>{count}</strong>
            </li>
          ))}
        </ul>
        {stats.total === 0 && <p className="crm-empty-line">{t('crm.stats.empty')}</p>}
      </section>

      <Table title={t('crm.stats.byPage')} rows={stats.by_page} t={t} />
      <Table title={t('crm.stats.bySource')} rows={stats.by_utm_source} t={t} />
      <Table title={t('crm.stats.lostReasons')} rows={stats.lost_reasons} t={t} />
    </div>
  );
}
